import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  AtsGetHiringTeamDocument,
  AtsRemoveHiringManagerDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SectionComponentLayout } from 'modules/ats/pages/JobPostingSettings/SectionComponentLayout'

import { AddHiringManagerModal } from './AddHiringManagerModal'
import { HiringManagerInfo } from './HiringManagerInfo'
import { HiringTeamSkeleton } from './HiringTeamSkeleton'

interface Props {
  jobId: string
  companyId: string
}

export const HiringTeam = ({ jobId, companyId }: Props) => {
  const [addHiringManagerOpen, setAddHiringManagerOpen] = useState(false)
  const { access } = useCurrentSession()

  const [destroyAtsHiringManager] = useMutation(AtsRemoveHiringManagerDocument, {
    extractorFn: (data) => data.atsMutations.destroyAtsHiringManager,
  })

  const { data, loading } = useQuery(AtsGetHiringTeamDocument, {
    variables: {
      jobId,
      companyId,
      accessId: access.id,
    },
  })

  useEffect(() => {
    track(trackingEvents.ats.jobSettingsAccessHiringTeam)
  }, [])

  const hiringManagers = data?.ats.hiringManagersConnection.nodes
  const hiringManagerRolesFeature =
    data?.companies.featureAvailabilitiesConnection.nodes.find(
      (feature) => feature.id === 'ats_customized_features'
    )?.available ?? false

  const canManageJobPostings = data?.ats._policies.manageJobPostings.can ?? false
  const currentHiringRole = data?.ats.currentHiringRole.nodes[0]?.hiringRole ?? 'editor'

  const removeHiringManager = async (id: string) => {
    const result = await destroyAtsHiringManager({
      variables: { id },
      update(cache) {
        cache.evict({ id: cache.identify({ id, __typename: 'AtsHiringManager' }) })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  return (
    <SectionComponentLayout>
      {loading || !hiringManagers ? (
        <HiringTeamSkeleton />
      ) : (
        <>
          <Box gap="s8">
            <Text size="400" color="grey1000" weight="semibold">
              {i18n.t('ats.job_posting_settings.hiring_team.title')}
            </Text>
            <Text size="300" color="grey800">
              {i18n.t('ats.job_posting_settings.hiring_team.description')}
            </Text>
            <Box paddingTop="s8">
              <Button onClick={() => setAddHiringManagerOpen(true)} iconName="add" type="secondary">
                {i18n.t('common.add')}
              </Button>
            </Box>
          </Box>

          <Box paddingTop="s40" gap="s16">
            {!hiringManagers.length && (
              <Box background="grey000White" justifyContent="center" width="full" padding="s32">
                <Text.Normal weight="medium">
                  {i18n.t('ats.job_posting_settings.hiring_team.not_found')}
                </Text.Normal>
              </Box>
            )}
            <HiringManagerInfo
              hiringManagers={hiringManagers}
              removeHiringManager={removeHiringManager}
              hasHiringManagerRolesFeature={hiringManagerRolesFeature}
              canManageJobPostings={canManageJobPostings}
              currentHiringRole={currentHiringRole}
              jobId={jobId}
            />
          </Box>
        </>
      )}

      {addHiringManagerOpen && (
        <AddHiringManagerModal jobId={jobId} onClose={() => setAddHiringManagerOpen(false)} />
      )}
    </SectionComponentLayout>
  )
}
